import { useContext, useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  DragNDrop,
  Input,
  Modal,
  Select,
  Text,
} from '@fairfashion/design-system'
import { Controller, useForm } from 'react-hook-form'
import ReactQuill from 'react-quill'

import { FeedbackAlertContext } from '../../../../components/FeedbackAlert'
import { QRCodeDetailsContext } from '../../qrcode-details.context'

import {
  QRCodeDetailsProductForm,
  QRCodeDetailsProductFormModalProps,
} from './qrcode-details-product-form-modal.types'

const statusOptions = [
  {
    label: 'Ativo',
    value: 'active',
  },
  {
    label: 'Inativo',
    value: 'inactive',
  },
]

export const QRCodeDetailsProductFormModal = ({
  open,
  onClose,
}: QRCodeDetailsProductFormModalProps) => {
  const [saving, setSaving] = useState(false)

  const [defaultImages, setDefaultImages] = useState<string[] | undefined>([])
  const [deletedImages, setDeletedImages] = useState<string[]>([])

  const { feedbackAlert } = useContext(FeedbackAlertContext)
  const { qrcode, finishedItemCategories, actions } =
    useContext(QRCodeDetailsContext)

  const categoriesOptions = useMemo(
    () =>
      finishedItemCategories.map((category) => ({
        label: category.name,
        value: category.id,
      })),
    [finishedItemCategories],
  )

  const { register, control, reset, handleSubmit, watch, setValue } =
    useForm<QRCodeDetailsProductForm>()

  useEffect(() => {
    if (open) {
      setDefaultImages(qrcode?.finishedItemProfile?.images)

      reset({
        code: qrcode?.code,
        name: qrcode?.finishedItemProfile?.name,
        description: qrcode?.finishedItemProfile?.description,
        qrcodeAccess: qrcode?.finishedItemProfile?.qrcodeAccess
          ? 'active'
          : 'inactive',
        finishedItemCategoryId:
          qrcode?.finishedItemProfile?.finishedItemCategoryId,
        images: [],
        externalLink: qrcode?.finishedItemProfile?.externalLink,
      })
    } else {
      setDefaultImages(undefined)
      reset({
        code: '',
        name: '',
        description: '',
        finishedItemCategoryId: '',
        images: [],
        externalLink: {
          link: '',
          label: '',
        },
      })
    }
  }, [open])

  const handleDeleteDefaultImage = (index: number) => {
    if (!defaultImages) return

    setDeletedImages([...deletedImages, defaultImages[index]])

    const newImages = [...defaultImages]
    newImages.splice(index, 1)
    setDefaultImages(newImages)
  }

  const onSubmit = async (data: QRCodeDetailsProductForm) => {
    setSaving(true)

    await actions.saveFinishedItemProfile(
      {
        code: data.code,
        name: data.name,
        description: data.description,
        qrcodeAccess: data.qrcodeAccess,
        finishedItemCategoryId: data.finishedItemCategoryId,
        externalLink:
          data.externalLink?.label || data.externalLink?.link
            ? data.externalLink
            : undefined,
      },
      data.images,
      deletedImages,
    )

    setSaving(false)
    onClose()
    feedbackAlert('Dados do produto salvos com sucesso', 'success')
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Dados do produto"
      width="800px"
      maxWidth="90%"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box width="100%" direction="column" gap={2} height="100%">
          <Box
            width="100%"
            direction="column"
            overflow="auto"
            flex={1}
            padding={2}
            gap={2}
          >
            {open && (
              <Box width="100%" direction="column">
                <Controller
                  name="images"
                  control={control}
                  render={({ field }) => (
                    <DragNDrop
                      onChange={(files) => field.onChange(files)}
                      defaultFiles={defaultImages}
                      onDeleteDefaultFile={handleDeleteDefaultImage}
                      preview="image"
                      fileType=".png, .jpg, .jpeg, .webp, image/png, image/jpeg, image/webp"
                      fileTypeError="Tipo de arquivo inválido"
                      maxSizeError="Arquivo muito grande. O limite do tamanho de arquivo é 5MB."
                      maxSize={5 * 1024 * 1024}
                      multiple
                    />
                  )}
                />
              </Box>
            )}

            <Box gap={2} width="100%">
              <Box flex={1}>
                <Input label="Código SKU" {...register('code')} disabled />
              </Box>
              <Box flex={1}>
                <Controller
                  name="finishedItemCategoryId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Categoria"
                      options={categoriesOptions}
                      placeholder="Selecione uma categoria"
                      {...field}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box gap={2} width="100%">
              <Box flex={1}>
                <Input label="Nome" {...register('name')} />
              </Box>
              <Box flex={1}>
                <Controller
                  name="qrcodeAccess"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Status"
                      options={statusOptions}
                      placeholder="Selecione o status"
                      {...field}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box direction="column" gap={1}>
              <Text size="small" color="grey_1">
                Descrição da Etapa
              </Text>
              <ReactQuill
                theme="snow"
                value={watch('description')}
                onChange={(content: string) => {
                  setValue('description', content)
                }}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['clean'],
                  ],
                }}
                style={{ height: '200px', marginBottom: '50px' }}
              />
            </Box>

            <Box gap={2} direction="column" width="100%" marginTop={2}>
              <Text size="small" fontWeight={500}>
                Link Externo
              </Text>
              <Box gap={2} width="100%">
                <Box flex={1}>
                  <Input
                    label="Link externo"
                    {...register('externalLink.link')}
                  />
                </Box>
                <Box flex={1}>
                  <Input
                    label="Texto do link externo"
                    {...register('externalLink.label')}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box width="100%" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={onClose} padding="10px 16px">
              <Text size="small" fontWeight={500}>
                Cancelar
              </Text>
            </Button>
            <Button padding="10px 16px" type="submit" loading={saving}>
              <Text size="small" fontWeight={500} color="white">
                Salvar
              </Text>
            </Button>
          </Box>
        </Box>
      </form>
    </Modal>
  )
}
